<script lang="ts">
  import {
    ensureHubspotAuthenticated,
    initializeHubspotOpportunityFields,
    syncOpportunityToHubspotById,
  } from "../lib/hubspotAuth";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import EngagementInvitationsTable from "./engagementInvitationsTable.svelte";

  const baseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;
  const aceBaseUrl = `${baseUrl}/alace`;
  const rejectionReasons = [
    "Customer problem unclear",
    "Next steps unclear",
    "Unable to support",
    "Duplicate of partner referral",
    "Other",
  ];
  const hubspotInvitationAuthWarning =
    "HubSpot is not connected. The invitation was accepted and imported, but HubSpot sync can be done later from the Opportunities table.";

  const refreshOpportunities = () => {
    triggerOpportunityDataRefresh();
  };

  const acceptInvitation = async (row: { id?: string }) => {
    if (!row?.id) {
      return { success: false, message: "Invitation identifier is missing." };
    }

    const res = await fetch(`${aceBaseUrl}/aws-partner-central/accept-engagement-invitation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identifier: row.id }),
    });

    return res.json();
  };

  const importAcceptedOpportunity = async (awsOpportunityId: string) => {
    const syncRes = await fetch(`${aceBaseUrl}/sync/from-aws/${awsOpportunityId}`, {
      method: "POST",
    });
    const result = await syncRes.json();
    if (syncRes.ok && result?.success !== false) {
      triggerOpportunityDataRefresh();
    }
    return result;
  };

  const ensureHubspotReady = async () => {
    const authenticated = await ensureHubspotAuthenticated();
    if (!authenticated) {
      return {
        success: false,
        authRequired: true,
        message: hubspotInvitationAuthWarning,
      };
    }

    const initResult = await initializeHubspotOpportunityFields({
      authRequiredMessage: hubspotInvitationAuthWarning,
      fallbackMessage:
        "HubSpot setup could not be completed. You can sync it later from the Opportunities table.",
      openDialogOnAuthError: false,
    });

    if (!initResult.success) {
      return {
        success: false,
        authRequired: initResult.authRequired,
        message: initResult.authRequired ? hubspotInvitationAuthWarning : initResult.message,
      };
    }

    return { success: true };
  };

  const syncAcceptedOpportunityToHubspot = async (localOpportunityId: number) => {
    const readiness = await ensureHubspotReady();
    if (!readiness.success) {
      return readiness;
    }

    return syncOpportunityToHubspotById(localOpportunityId, {
      authRequiredMessage: hubspotInvitationAuthWarning,
      fallbackMessage:
        "HubSpot sync could not be completed. You can retry it later from the Opportunities table.",
      openDialogOnAuthError: false,
    });
  };

  const rejectInvitation = async (row: {
    id?: string;
    rejectionReason?: string;
  }) => {
    if (!row?.id) {
      return { success: false, message: "Invitation identifier is missing." };
    }

    if (!row.rejectionReason) {
      return { success: false, message: "Rejection reason is required." };
    }

    const res = await fetch(`${aceBaseUrl}/aws-partner-central/reject-engagement-invitation`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        identifier: row.id,
        rejectionReason: row.rejectionReason,
      }),
    });

    return res.json();
  };
</script>

<div class="w-full">
  <EngagementInvitationsTable
    title="Pending Engagement Invitations"
    {rejectionReasons}
    onAccept={acceptInvitation}
    onImportAcceptedOpportunity={importAcceptedOpportunity}
    onRefreshOpportunities={refreshOpportunities}
    onSyncAcceptedOpportunityToHubspot={syncAcceptedOpportunityToHubspot}
    onReject={rejectInvitation}
  />
</div>
